import { useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Calendar } from '@styled-icons/remix-line';

import { useQuery } from 'v3/hooks/useQuery';
import { Col, Container, Row } from 'components/GridSystem';
import Footer from 'components/Footer';
import Header from 'components/Header';
import FloatButtonBar from 'components/FloatButtonBar';
import FormField from 'components/FormField';
import MaskField from 'components/MaskField';
import theme from 'styles/theme';
import * as S from './styles';
import { useConnectCreditCard } from 'hooks/useConnectCreditCard';
import LoadingSpinner from 'components/LoadingSpinner';
import { setLeadData } from 'leadData/leadSlice';
import { useDispatch } from 'react-redux';
import CardBreakdownTable from 'components/CardBreakdownTable';

const ConnectCreditCardSchema = Yup.object().shape({
  name: Yup.string().required('This field is required'),
  number: Yup.string()
    .required('This field is required')
    .min(18, 'The card number should have at least 15 numbers')
    .max(19, 'the card number should have a maximum of 16 numbers'),
  cvc: Yup.string()
    .required('This field required')
    .min(3, 'The CVV should have at least 3 numbers')
    .max(4, 'The CVV should have a maximum of 4 numbers'),
  date: Yup.string()
    .required('This field is required')
    .test(
      'is-month-valid',
      'Please write a valid month',
      (value: string | undefined) => {
        if (value) {
          const [month] = value.split('/');
          return Number(month) > 0 && Number(month) < 13;
        }
        return false;
      }
    )
    .test(
      'is-year-valid',
      'Please write a valid year',
      (value: string | undefined) => {
        if (value) {
          const [, year] = value.split('/');
          const fullYear = 2000 + Number(year);
          return fullYear >= new Date().getFullYear();
        }
        return false;
      }
    ),
});

const ConnectCreditCard = () => {
  const query = useQuery();
  const userUuid = query.get('userUuid') ?? '';
  const cfUuid = query.get('cfUuid') ?? '';
  const email = query.get('email');
  const clubId = query.get('clubId') ? Number(query.get('clubId')) : 0;

  const { loading, paymentInformation, handleValidateData } =
    useConnectCreditCard({ userUuid, clubId, cfUuid });
  const dispatch = useDispatch();

  useEffect(() => {
    if (email) {
      dispatch(setLeadData({ leadData: { email } }));
    }
  }, [email, dispatch]);

  return (
    <S.Wrapper>
      <Formik
        initialValues={{
          name: '',
          number: '',
          cvc: '',
          date: '',
        }}
        validationSchema={ConnectCreditCardSchema}
        onSubmit={async (values) => await handleValidateData(values)}
      >
        {(formik) => {
          const { errors, touched } = formik;

          return (
            <>
              <Header variant="primary" />

              <S.Main>
                <S.PaymentInformationContainer>
                  <Row>
                    <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                      <S.Title>Payment Details</S.Title>
                      <S.PaymentInformationContent>
                        <S.Info>
                          <S.InfoTitle>For payment:</S.InfoTitle>
                          <S.InfoSubtitle>
                            Fill in your card details to activate your account
                          </S.InfoSubtitle>
                        </S.Info>
                      </S.PaymentInformationContent>
                    </Col>
                  </Row>
                </S.PaymentInformationContainer>
                <Container>
                  <Row>
                    <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                      {paymentInformation?.debit ? (
                        <CardBreakdownTable debit={paymentInformation.debit} />
                      ) : (
                        <LoadingSpinner />
                      )}

                      <S.Form>
                        <FormField
                          name="name"
                          placeholder="Full name on card"
                          label="Full Name on card"
                          isValid={!(touched.name && errors.name)}
                          feedbackMessage={touched.name && errors.name}
                          required
                        />
                        <MaskField
                          isValid={!(touched.number && errors.number)}
                          feedbackMessage={touched.number && errors.number}
                          name="number"
                          placeholder="0000 0000 0000 0000"
                          label="Card Number"
                          icon={
                            <img
                              width="81px"
                              height="25px"
                              src="/assets/svg/card-flags.svg"
                              alt="Credit Card Flags"
                            />
                          }
                          mask="9999 9999 9999 9999"
                          type="number"
                          required
                        />
                        <FormField
                          inputMode="numeric"
                          isValid={!(touched.cvc && errors.cvc)}
                          feedbackMessage={touched.cvc && errors.cvc}
                          name="cvc"
                          placeholder="CVV"
                          label="Code"
                          required
                        />
                        <MaskField
                          isValid={!(touched.date && errors.date)}
                          feedbackMessage={touched.date && errors.date}
                          label="Expiration Date"
                          name="date"
                          placeholder="MM/YY"
                          mask="99/99"
                          required
                          icon={
                            <Calendar
                              size={24}
                              color={theme.colors.neutralGray80}
                            />
                          }
                        />
                        <FloatButtonBar
                          showVehicleBadge={false}
                          value="30,000"
                          label={loading ? <LoadingSpinner /> : 'pay now ›'}
                          img="/assets/svg/vehicle-icon.svg"
                          title="30K Club"
                        />
                      </S.Form>
                    </Col>
                  </Row>
                </Container>
              </S.Main>

              <Footer showPolicyAndTerms />
            </>
          );
        }}
      </Formik>
    </S.Wrapper>
  );
};

export default ConnectCreditCard;
